import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Star, Swords, Target, Users, User } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@v2/components/ui/tabs";
import { supabaseV2 } from "../../lib/supabase";
import { Sparkline } from "../../components/charts";
import { EmptyState } from "../../components/state";
import { FcIcon } from "../../components/brand";
import { recordMatchesPlayer } from "../../lib/ea-name";
import { currentIntlLocale } from "../../lib/i18n";
import { ourClubIdOf } from "../../lib/ea-game";
import { PlayerRoleComparison } from "./PlayerRoleComparison";
import { PlayerCv, type CvAggregate, type CvCareer, type CvTopMatch } from "./PlayerCv";
import { TeamLeaderboard } from "./TeamLeaderboard";

export function PlayerEaTab({
  teamId,
  eaCandidates,
  emptyText,
  playerId,
  displayName,
  roleCode,
  onOpenPlayer,
}: {
  teamId: string;
  // Tutti gli identificatori EA noti del giocatore (ea_sports_id, ea_account_handle).
  // Il match e' delegato a recordMatchesPlayer (lib/ea-name.ts), che usa la stessa
  // lista di campi-record (name/playername/proName) di resolveEaPlayerName.
  eaCandidates: string[];
  // Testo da mostrare quando il player non ha eaCandidates. Default scritto
  // per la vista founder ("aggiorna dal pulsante Modifica"); la vista player
  // passa un messaggio adatto al destinatario ("chiedi al coach").
  emptyText?: string;
  // playerId della rosa: serve per il pannello "Confronto per ruolo".
  playerId?: string;
  // Nome leggibile del giocatore (usato dal CV). Passato dal caller perché
  // dentro PlayerEaTab non viene fetchato il record `players`.
  displayName?: string;
  // Ruolo di rosa (POR/DC/...). Mostrato come etichetta nel CV.
  roleCode?: string | null;
  // Callback per aprire la scheda di un compagno dal pannello di confronto.
  onOpenPlayer?: (id: string) => void;
}) {
  const { t } = useTranslation("mondofc");
  const enabled = eaCandidates.length > 0;
  const candidatesLower = eaCandidates.map((c) => c.toLowerCase());
  // Per la query `ea-member` proviamo il primo candidate (di solito ea_sports_id).
  const primaryHandle = eaCandidates[0] || null;
  const memberQuery = useQuery({
    queryKey: ["ea-member", teamId, candidatesLower.join("|")],
    queryFn: async () => {
      if (!enabled) return null;
      // RPC accetta una sola persona: provo i candidates in ordine, prendo il primo che torna dato.
      for (const handle of eaCandidates) {
        const { data, error } = await supabaseV2.rpc("latest_ea_member", {
          _team_id: teamId,
          _ea_persona: handle,
        });
        if (error) {
          console.warn("[PlayerEaTab] latest_ea_member error for", handle, error);
          continue;
        }
        if (data) return data as any;
      }
      return null;
    },
    enabled,
  });
  const matchesQuery = useQuery({
    queryKey: ["ea-matches-player", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("ea_match_snapshots")
        .select("ea_match_id, played_at, data")
        .eq("team_id", teamId)
        .order("played_at", { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      return (data || []) as Array<{ ea_match_id: string; played_at: string | null; data: any }>;
    },
    enabled,
  });
  const teamQuery = useQuery({
    queryKey: ["team-ea-club-id", teamId],
    queryFn: async () => {
      const { data } = await supabaseV2.from("teams").select("ea_club_id").eq("id", teamId).maybeSingle();
      return data?.ea_club_id as string | null;
    },
    enabled,
  });

  if (!enabled) {
    return (
      <Card className="shadow-card mt-4">
        <CardContent className="p-6">
          <EmptyState text={emptyText || t("playerTab.emptyDefault")} />
        </CardContent>
      </Card>
    );
  }

  const eaClubId = teamQuery.data;
  const m = memberQuery.data;
  const eaPersona = m?.name || m?.proName || primaryHandle || "";

  // Diagnostica: raccoglie tutti i nomi distinti che appaiono come player nel club EA dei match
  // (per spiegare perché un candidato non matcha).
  const allKnownNames = new Set<string>();

  // Conteggio totali per matchType (denominatore: tutti i match scaricati, NON solo
  // quelli con il record del player).
  const teamMatchCounts = { season: 0, friendly: 0 };
  for (const row of matchesQuery.data || []) {
    const t = String(row.data?.match_type || "");
    if (t === "leagueMatch" || t === "playoffMatch") teamMatchCounts.season += 1;
    else if (t === "friendlyMatch") teamMatchCounts.friendly += 1;
  }

  // Estrai per ogni match il sub-record del player corrente, se presente.
  const playerMatchEntries = (matchesQuery.data || [])
    .map((row) => {
      if (!eaClubId) return null;
      const ourId = ourClubIdOf(row.data, eaClubId);
      const players = row.data?.players?.[ourId] || {};
      const playerArr = Object.values(players) as any[];
      for (const p of playerArr) {
        const display = p?.proName || p?.playername || p?.name;
        if (display) allKnownNames.add(String(display));
      }
      // Match: confronta i candidates del player (ea_sports_id, ea_account_handle)
      // contro `name`, `playername`, `proName` di ogni record EA, via recordMatchesPlayer.
      const me = playerArr.find((p: any) => recordMatchesPlayer(p, candidatesLower)) as any;
      if (!me) return null;
      const seconds = Number(me.secondsPlayed || 0);
      if (seconds <= 0) return null;
      const oppId = Object.keys(row.data?.clubs || {}).find((id) => id !== ourId);
      const ourClub = row.data?.clubs?.[ourId];
      const oppClub = oppId ? row.data?.clubs?.[oppId] : null;
      return {
        ea_match_id: row.ea_match_id,
        played_at: row.played_at,
        match_type: row.data?.match_type as string,
        opponent_name: oppClub?.details?.name || (oppId ? t("matchList.clubFallback", { id: oppId }) : "-"),
        our_goals: Number(ourClub?.goals || 0),
        opp_goals: Number(oppClub?.goals || 0),
        me,
      };
    })
    .filter(Boolean) as Array<{
      ea_match_id: string;
      played_at: string | null;
      match_type: string;
      opponent_name: string;
      our_goals: number;
      opp_goals: number;
      me: any;
    }>;

  // Aggregato per categoria
  const aggregate = (types: string[]) => {
    const subset = playerMatchEntries.filter((r) => types.includes(r.match_type));
    let goals = 0,
      assists = 0,
      mom = 0,
      ratingSum = 0,
      ratingCount = 0,
      shots = 0,
      passesMade = 0,
      passAttempts = 0,
      tacklesMade = 0,
      tackleAttempts = 0,
      saves = 0,
      cleanSheets = 0,
      wins = 0,
      reds = 0,
      minutes = 0;
    for (const r of subset) {
      const p = r.me;
      goals += Number(p.goals || 0);
      assists += Number(p.assists || 0);
      mom += Number(p.mom || 0);
      const rt = Number(p.rating || 0);
      if (rt > 0) {
        ratingSum += rt;
        ratingCount += 1;
      }
      shots += Number(p.shots || 0);
      passesMade += Number(p.passesmade || 0);
      passAttempts += Number(p.passattempts || 0);
      tacklesMade += Number(p.tacklesmade || 0);
      tackleAttempts += Number(p.tackleattempts || 0);
      saves += Number(p.saves || 0);
      if (Number(p.cleansheetsany || 0) > 0) cleanSheets += 1;
      reds += Number(p.redcards || 0);
      minutes += Math.round(Number(p.secondsPlayed || 0) / 60);
      if (r.our_goals > r.opp_goals) wins += 1;
    }
    const games = subset.length;
    return {
      games,
      goals,
      assists,
      mom,
      reds,
      ratingAvg: ratingCount > 0 ? ratingSum / ratingCount : 0,
      shots,
      passesMade,
      passAttempts,
      passPct: passAttempts > 0 ? Math.round((passesMade / passAttempts) * 100) : 0,
      tacklesMade,
      tackleAttempts,
      tackleSuccessPct: tackleAttempts > 0 ? Math.round((tacklesMade / tackleAttempts) * 100) : 0,
      saves,
      cleanSheets,
      winPct: games > 0 ? Math.round((wins / games) * 100) : 0,
      minutes,
      subset,
    };
  };

  const all = aggregate(["leagueMatch", "playoffMatch", "friendlyMatch"]);
  const season = aggregate(["leagueMatch", "playoffMatch"]);
  const friendly = aggregate(["friendlyMatch"]);

  // Top match (best by rating, by goals)
  const bestRating = [...all.subset].sort((a, b) => Number(b.me.rating || 0) - Number(a.me.rating || 0))[0];
  const bestGoals = [...all.subset].sort((a, b) => Number(b.me.goals || 0) - Number(a.me.goals || 0))[0];

  // Trend rating ultime 12 partite (cronologiche, dal più vecchio)
  const trendRating = all.subset.slice(0, 12).reverse().map((r) => Number(r.me.rating || 0));
  const trendGoalsArr = all.subset.slice(0, 12).reverse().map((r) => Number(r.me.goals || 0));

  return (
    <div className="space-y-4">
      {/* Hero EA */}
      <Card className="shadow-card hud-card">
        <CardContent className="p-5 flex items-center gap-4 flex-wrap">
          <div className="lms-tile-icon tone-lilac"><FcIcon className="h-7 w-7" /></div>
          <div className="min-w-0 flex-1">
            <p className="eb-eyebrow">{t("playerTab.profile")}</p>
            <h2 className="text-2xl font-bold tracking-tight truncate">
              {m?.proName || eaPersona}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("playerTab.accountLabel")}: <span className="font-mono">{eaPersona}</span>
              {m?.proOverall ? ` · OVR ${m.proOverall}` : ""}
              {m?.favoritePosition ? ` · ${m.favoritePosition}` : ""}
            </p>
          </div>
          {m?.ratingAve && (
            <div className="rounded-xl border bg-card px-4 py-3 text-center min-w-[110px]">
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground font-semibold">{t("playerTab.avgRating")}</p>
              <p className="font-mono text-2xl font-bold tabular-nums leading-none mt-1">{Number(m.ratingAve).toFixed(2)}</p>
              <p className="text-[10.5px] text-muted-foreground mt-1">{t("playerTab.allTime")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {!matchesQuery.isLoading && all.games === 0 && (matchesQuery.data?.length || 0) === 0 && (
        <Card className="shadow-card">
          <CardContent className="p-6 space-y-3">
            <EmptyState text={t("playerTab.noMatches")} />
            <p className="text-xs text-muted-foreground">
              {t("playerTab.noMatchesHint")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Carriera club aggregata: i veri totali "ufficiali" lato EA (cumulativi nel club).
          NON sono separabili per Season/Friendly perché EA li espone solo come unico cumulato. */}
      {m && Number(m.gamesPlayed || 0) > 0 && (
        <Card className="shadow-card hud-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FcIcon className="h-4 w-4 text-violet-500" />
              {t("playerTab.careerTitle")}
            </CardTitle>
            <CardDescription>
              {t("playerTab.careerDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
              <div className="pcell"><span className="stat-label">{t("playerTab.matches")}</span><span className="stat-value">{Number(m.gamesPlayed || 0)}</span></div>
              <div className="pcell pcell-ok"><span className="stat-label">{t("playerTab.goals")}</span><span className="stat-value">{Number(m.goals || 0)}</span></div>
              <div className="pcell pcell-warn"><span className="stat-label">{t("playerTab.assists")}</span><span className="stat-value">{Number(m.assists || 0)}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.avgRating")}</span><span className="stat-value">{m.ratingAve ? Number(m.ratingAve).toFixed(2) : "-"}</span></div>
              <div className="pcell pcell-muted"><span className="stat-label">{t("playerTab.winPct")}</span><span className="stat-value">{Number(m.winRate || 0)}%</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.mom")}</span><span className="stat-value">{Number(m.mom || 0)}</span></div>
            </div>
          </CardContent>
        </Card>
      )}

      {all.games > 0 && (
        <Tabs defaultValue="me" className="w-full">
          <TabsList className="grid grid-cols-3 w-full sm:w-auto sm:inline-flex">
            <TabsTrigger
              value="me"
              className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <User className="h-3.5 w-3.5" />
              <span>{t("playerTab.sections.me")}</span>
            </TabsTrigger>
            <TabsTrigger
              value="versus"
              disabled={!playerId}
              className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Swords className="h-3.5 w-3.5" />
              <span>{t("playerTab.sections.versus")}</span>
            </TabsTrigger>
            <TabsTrigger
              value="team"
              className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Users className="h-3.5 w-3.5" />
              <span>{t("playerTab.sections.team")}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="me" className="space-y-4 mt-4">
            {/* CV condivisibile: prima card del tab "My Player". */}
            <PlayerCv
              displayName={displayName || m?.proName || eaPersona}
              rolesAffinity={roleCode || null}
              eaPersona={eaPersona}
              career={
                m && Number(m.gamesPlayed || 0) > 0
                  ? ({
                      gamesPlayed: Number(m.gamesPlayed || 0),
                      goals: Number(m.goals || 0),
                      assists: Number(m.assists || 0),
                      mom: Number(m.mom || 0),
                      winRate: Number(m.winRate || 0),
                      ratingAve: Number(m.ratingAve || 0),
                      proName: m.proName,
                      proOverall: m.proOverall ? Number(m.proOverall) : null,
                      favoritePosition: m.favoritePosition,
                    } as CvCareer)
                  : null
              }
              season={toCvAgg(season)}
              friendly={toCvAgg(friendly)}
              all={toCvAgg(all)}
              bestRating={
                bestRating
                  ? ({
                      date: bestRating.played_at,
                      opponentName: bestRating.opponent_name,
                      ourGoals: bestRating.our_goals,
                      oppGoals: bestRating.opp_goals,
                      rating: Number(bestRating.me.rating || 0),
                      goals: Number(bestRating.me.goals || 0),
                      assists: Number(bestRating.me.assists || 0),
                    } as CvTopMatch)
                  : null
              }
              bestGoals={
                bestGoals && Number(bestGoals.me.goals) > 0
                  ? ({
                      date: bestGoals.played_at,
                      opponentName: bestGoals.opponent_name,
                      ourGoals: bestGoals.our_goals,
                      oppGoals: bestGoals.opp_goals,
                      rating: Number(bestGoals.me.rating || 0),
                      goals: Number(bestGoals.me.goals || 0),
                      assists: Number(bestGoals.me.assists || 0),
                    } as CvTopMatch)
                  : null
              }
            />

            {/* Dettaglio match scaricati: separa Season da Friendly. */}
            <div className="grid lg:grid-cols-2 gap-4">
              <FullEaStatsCard
                title={t("playerTab.seasonTitle")}
                subtitle={t("playerTab.seasonSubtitle")}
                note={t("playerTab.seasonNote")}
                tone="primary"
                agg={season}
                teamTotal={teamMatchCounts.season}
              />
              <FullEaStatsCard
                title={t("playerTab.friendlyTitle")}
                subtitle={t("playerTab.friendlySubtitle")}
                note={t("playerTab.friendlyNote")}
                tone="muted"
                agg={friendly}
                teamTotal={teamMatchCounts.friendly}
              />
            </div>

            {/* Trend ultime 12 */}
          {trendRating.length >= 2 && (
            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="shadow-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{t("playerTab.ratingTrendTitle", { count: trendRating.length })}</CardTitle>
                  <CardDescription>{t("playerTab.ratingTrendDesc")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Sparkline values={trendRating} color="hsl(180 60% 40%)" className="w-full h-12" />
                </CardContent>
              </Card>
              <Card className="shadow-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{t("playerTab.goalsTrendTitle", { count: trendGoalsArr.length })}</CardTitle>
                  <CardDescription>{t("playerTab.goalsTrendDesc")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Sparkline values={trendGoalsArr} color="hsl(150 50% 50%)" className="w-full h-12" />
                </CardContent>
              </Card>
            </div>
          )}


          {/* Top match */}
          {(bestRating || bestGoals) && (
            <div className="grid md:grid-cols-2 gap-4">
              {bestRating && (
                <Card className="shadow-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500" />
                      {t("playerTab.bestRatingTitle")}
                    </CardTitle>
                    <CardDescription>{t("playerTab.bestRatingDesc")}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="font-medium">{t("playerTab.vsLabel")} {bestRating.opponent_name}</p>
                    <p className="text-[11px] text-muted-foreground font-mono">
                      {bestRating.played_at ? new Date(bestRating.played_at).toLocaleDateString(currentIntlLocale()) : "-"} · {bestRating.match_type}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-sm">
                      <span className={cn("score-chip", bestRating.our_goals > bestRating.opp_goals ? "win" : bestRating.our_goals < bestRating.opp_goals ? "lose" : "draw")}>
                        {bestRating.our_goals}-{bestRating.opp_goals}
                      </span>
                      <span className="text-muted-foreground">{t("playerTab.bestRatingScoreLabel")}</span>
                      <span className="font-mono font-bold text-lg">{Number(bestRating.me.rating || 0).toFixed(2)}</span>
                      {Number(bestRating.me.goals) > 0 && <span className="tag-mono">{bestRating.me.goals} G</span>}
                      {Number(bestRating.me.assists) > 0 && <span className="tag-mono">{bestRating.me.assists} A</span>}
                    </div>
                  </CardContent>
                </Card>
              )}
              {bestGoals && Number(bestGoals.me.goals) > 0 && (
                <Card className="shadow-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4 text-emerald-600" />
                      {t("playerTab.hatTrickTitle")}
                    </CardTitle>
                    <CardDescription>{t("playerTab.hatTrickDesc")}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p className="font-medium">{t("playerTab.vsLabel")} {bestGoals.opponent_name}</p>
                    <p className="text-[11px] text-muted-foreground font-mono">
                      {bestGoals.played_at ? new Date(bestGoals.played_at).toLocaleDateString(currentIntlLocale()) : "-"} · {bestGoals.match_type}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-sm">
                      <span className={cn("score-chip", bestGoals.our_goals > bestGoals.opp_goals ? "win" : bestGoals.our_goals < bestGoals.opp_goals ? "lose" : "draw")}>
                        {bestGoals.our_goals}-{bestGoals.opp_goals}
                      </span>
                      <span className="font-mono font-bold text-lg text-emerald-600">{bestGoals.me.goals} G</span>
                      {Number(bestGoals.me.assists) > 0 && <span className="tag-mono">{bestGoals.me.assists} A</span>}
                      <span className="text-muted-foreground">{t("playerTab.ratingLine", { value: Number(bestGoals.me.rating || 0).toFixed(2) })}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Lista match recenti */}
          <Card className="shadow-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("playerTab.recentTitle")}</CardTitle>
              <CardDescription>{t("playerTab.recentDesc", { count: Math.min(15, all.subset.length) })}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left font-mono uppercase text-[10.5px] tracking-wider py-1 pr-2">{t("playerTab.th.date")}</th>
                      <th className="text-left font-mono uppercase text-[10.5px] tracking-wider px-1">{t("playerTab.th.type")}</th>
                      <th className="text-left font-mono uppercase text-[10.5px] tracking-wider px-1">{t("playerTab.th.opponent")}</th>
                      <th className="text-center font-mono uppercase text-[10.5px] tracking-wider px-1">{t("playerTab.th.result")}</th>
                      <th className="text-right font-mono uppercase text-[10.5px] tracking-wider px-1">{t("playerTab.th.g")}</th>
                      <th className="text-right font-mono uppercase text-[10.5px] tracking-wider px-1">{t("playerTab.th.a")}</th>
                      <th className="text-right font-mono uppercase text-[10.5px] tracking-wider px-1 hidden md:table-cell">{t("playerTab.th.mom")}</th>
                      <th className="text-right font-mono uppercase text-[10.5px] tracking-wider px-1">{t("playerTab.th.rating")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {all.subset.slice(0, 15).map((r) => {
                      const cls = r.our_goals > r.opp_goals ? "win" : r.our_goals < r.opp_goals ? "lose" : "draw";
                      const typeLabel =
                        r.match_type === "friendlyMatch" ? t("playerTab.typeShort.friendlyMatch") :
                        r.match_type === "playoffMatch" ? t("playerTab.typeShort.playoffMatch") :
                        r.match_type === "leagueMatch" ? t("playerTab.typeShort.leagueMatch") :
                        "-";
                      return (
                        <tr key={r.ea_match_id} className="border-t hover:bg-muted/40">
                          <td className="py-1.5 pr-2 font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                            {r.played_at ? new Date(r.played_at).toLocaleDateString(currentIntlLocale(), { day: "2-digit", month: "short", year: "2-digit" }) : "-"}
                          </td>
                          <td className="px-1"><span className="tag-mono">{typeLabel}</span></td>
                          <td className="px-1 truncate max-w-[160px]">{r.opponent_name}</td>
                          <td className="px-1 text-center"><span className={cn("score-chip", cls)}>{r.our_goals}-{r.opp_goals}</span></td>
                          <td className="text-right font-tabular px-1 text-emerald-600">{r.me.goals || 0}</td>
                          <td className="text-right font-tabular px-1 text-amber-600">{r.me.assists || 0}</td>
                          <td className="text-right font-tabular px-1 hidden md:table-cell">{Number(r.me.mom || 0) > 0 ? "★" : ""}</td>
                          <td className="text-right font-tabular px-1 font-bold">{Number(r.me.rating || 0).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="versus" className="space-y-4 mt-4">
            {/* Confronto per ruolo: KPI vs media squadra + classifica
                compagni che hanno ricoperto lo stesso ruolo almeno N volte.
                Visibile sia in vista coach sia in vista player self (la RPC
                è gated a founder/coach/captain/player). */}
            {playerId ? (
              <PlayerRoleComparison
                teamId={teamId}
                playerId={playerId}
                onOpenPlayer={onOpenPlayer}
              />
            ) : (
              <Card className="shadow-card">
                <CardContent className="py-8">
                  <EmptyState text={t("playerTab.versusEmpty")} />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="team" className="space-y-4 mt-4">
            {playerId ? (
              <TeamLeaderboard
                teamId={teamId}
                playerId={playerId}
                onOpenPlayer={onOpenPlayer}
              />
            ) : (
              <Card className="shadow-card">
                <CardContent className="py-8">
                  <EmptyState text={t("playerTab.teamComingSoon")} />
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// Mapping da EaAggregate (interno) a CvAggregate (atteso dal CV).
function toCvAgg(a: EaAggregate): CvAggregate {
  return {
    games: a.games,
    goals: a.goals,
    assists: a.assists,
    mom: a.mom,
    ratingAvg: a.ratingAvg,
    winPct: a.winPct,
    minutes: a.minutes,
  };
}

type EaAggregate = {
  games: number;
  goals: number;
  assists: number;
  mom: number;
  reds: number;
  ratingAvg: number;
  shots: number;
  passesMade: number;
  passAttempts: number;
  passPct: number;
  tacklesMade: number;
  tackleAttempts: number;
  tackleSuccessPct: number;
  saves: number;
  cleanSheets: number;
  winPct: number;
  minutes: number;
};

function FullEaStatsCard({
  title,
  subtitle,
  note,
  tone,
  agg,
  teamTotal,
}: {
  title: string;
  subtitle: string;
  note?: string;
  tone: "primary" | "muted";
  agg: EaAggregate;
  teamTotal: number;
}) {
  const { t } = useTranslation("mondofc");
  return (
    <Card className={cn("shadow-card", tone === "primary" && "hud-card")}>
      <CardHeader className="pb-3 space-y-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FcIcon className={cn("h-4 w-4", tone === "primary" ? "text-violet-500" : "text-muted-foreground")} />
          {title}
        </CardTitle>
        {note && (
          <CardDescription className="text-xs leading-relaxed">{note}</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {agg.games === 0 ? (
          <EmptyState text={t("playerTab.noStarter")} />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <div className="pcell"><span className="stat-label">{t("playerTab.matches")}</span><span className="stat-value">{agg.games}</span></div>
              <div className="pcell pcell-ok"><span className="stat-label">{t("playerTab.goals")}</span><span className="stat-value">{agg.goals}</span></div>
              <div className="pcell pcell-warn"><span className="stat-label">{t("playerTab.assists")}</span><span className="stat-value">{agg.assists}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.mom")}</span><span className="stat-value">{agg.mom}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.avgRating")}</span><span className="stat-value">{agg.ratingAvg > 0 ? agg.ratingAvg.toFixed(2) : "-"}</span></div>
              <div className="pcell pcell-muted"><span className="stat-label">{t("playerTab.winPct")}</span><span className="stat-value">{agg.winPct}%</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.shots")}</span><span className="stat-value">{agg.shots}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.passPct")}</span><span className="stat-value">{agg.passPct}%</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.passes")}</span><span className="stat-value">{agg.passesMade}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.tacklePct")}</span><span className="stat-value">{agg.tackleSuccessPct}%</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.tackles")}</span><span className="stat-value">{agg.tacklesMade}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.saves")}</span><span className="stat-value">{agg.saves}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.cleanSheets")}</span><span className="stat-value">{agg.cleanSheets}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.reds")}</span><span className="stat-value">{agg.reds}</span></div>
              <div className="pcell"><span className="stat-label">{t("playerTab.stat.minutes")}</span><span className="stat-value tabular-nums">{agg.minutes}'</span></div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2.5 text-[11px]">
              <div className="rounded-lg bg-muted/40 px-2 py-1.5">
                <span className="text-muted-foreground">{t("playerTab.perGame.g")}</span>
                <span className="ml-1 font-mono font-semibold">{agg.games > 0 ? (agg.goals / agg.games).toFixed(2) : "0"}</span>
              </div>
              <div className="rounded-lg bg-muted/40 px-2 py-1.5">
                <span className="text-muted-foreground">{t("playerTab.perGame.a")}</span>
                <span className="ml-1 font-mono font-semibold">{agg.games > 0 ? (agg.assists / agg.games).toFixed(2) : "0"}</span>
              </div>
              <div className="rounded-lg bg-muted/40 px-2 py-1.5">
                <span className="text-muted-foreground">{t("playerTab.perGame.mom")}</span>
                <span className="ml-1 font-mono font-semibold">{agg.games > 0 ? Math.round((agg.mom / agg.games) * 100) : 0}%</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
